import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Paper,
  Divider,
  Chip,
  useTheme,
  useMediaQuery,
  Stack,
} from '@mui/material';
import {
  School as SchoolIcon,
  Group as GroupIcon,
  Home as HomeIcon,
  Public as PublicIcon,
  Launch as LaunchIcon,
  Favorite as FavoriteIcon,
} from '@mui/icons-material';
import membershipIcon from '../../assets/Membership_Icon.png';

const BRAND = '#C8102E';
const BRAND_DARK = '#A00D25';
const LIBRARY_URL = '/library';

type ResourceLink = {
  label: string;
  url: string;
};

type ResourceCard = {
  title: string;
  description: string;
  icon: React.ReactNode;
  links: ResourceLink[];
};

const MembershipResources: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const cards: ResourceCard[] = [
    {
      title: 'New Members',
      description:
        'Welcome! Your go-to spot for onboarding, essential resources and getting involved for new and returning sisters.',
      icon: <SchoolIcon />,
      links: [
        { label: 'Onboarding', url: LIBRARY_URL },
        { label: 'Getting Connected', url: LIBRARY_URL },
        { label: 'Projects', url: LIBRARY_URL },
      ],
    },
    {
      title: 'Membership Resources',
      description:
        'Tools to help you engage, encourage and grow our Sisterhood. For all members, officers and membership committees.',
      icon: <GroupIcon />,
      links: [{ label: 'Resources', url: LIBRARY_URL }],
    },
    {
      title: 'Local Chapters Resources',
      description: 'Placeholder',
      icon: <HomeIcon />,
      links: [{ label: 'Resources', url: LIBRARY_URL }],
    },
    {
      title: 'S/P/D Resources',
      description: 'Placeholder',
      icon: <PublicIcon />,
      links: [{ label: 'Resources', url: LIBRARY_URL }],
    },
  ];

  const openLink = (url: string) => {
    window.open(url, '_blank');
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      <Box
        sx={{
          background: `linear-gradient(135deg, #fff 0%, ${BRAND} 100%)`,
          color: 'white',
          py: 8,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={10}>
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  fontWeight: 700,
                  mb: 3,
                  fontSize: isMobile ? '2.5rem' : '3.5rem',
                  lineHeight: 1.2,
                }}
              >
                Membership Resources
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  opacity: 0.9,
                  lineHeight: 1.6,
                  maxWidth: '1000px',
                }}
              >
                Shortcuts to the tools and library materials that help sisters
                onboard, stay connected, and grow membership in every chapter.
              </Typography>
              <Chip
                label="P.E.O. Membership"
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  fontSize: '1rem',
                  py: 1,
                  px: 2,
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={6}>
          <Grid item xs={12} lg={4}>
            <Card elevation={3} sx={{ height: '100%', borderRadius: 3 }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box
                    component="img"
                    src={membershipIcon}
                    alt="P.E.O. Membership"
                    sx={{
                      width: '180px',
                      height: 'auto',
                    }}
                  />
                </Box>

                <Divider sx={{ my: 3 }} />

                <Typography
                  variant="h5"
                  component="h3"
                  sx={{ mb: 3, color: BRAND, fontWeight: 600 }}
                >
                  About Membership Resources
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                  This hub gathers the most-used membership materials in one
                  place—whether you are welcoming new sisters, supporting a
                  local chapter, or leading at the S/P/D level.
                </Typography>

                <Divider sx={{ my: 3 }} />

                <Typography
                  variant="h6"
                  sx={{ mb: 2, color: BRAND, fontWeight: 600 }}
                >
                  How to Use These Resources
                </Typography>
                <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                  Select a button on a card to open related library materials in
                  a new tab. Bundle destinations will be refined as content is
                  finalized.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} lg={8}>
            <Grid container spacing={3}>
              {cards.map((card) => (
                <Grid item xs={12} sm={6} key={card.title}>
                  <Card
                    elevation={2}
                    sx={{
                      height: '100%',
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      flexDirection: 'column',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 6,
                        border: `2px solid ${BRAND}`,
                      },
                    }}
                  >
                    <CardContent
                      sx={{
                        p: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mb: 1.5,
                        }}
                      >
                        <Box sx={{ color: BRAND, mr: 2 }}>{card.icon}</Box>
                        <Typography
                          variant="h6"
                          component="h4"
                          sx={{ fontWeight: 600, flex: 1 }}
                        >
                          {card.title}
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          mb: 2,
                          flex: 1,
                          lineHeight: 1.6,
                          color: 'text.secondary',
                        }}
                      >
                        {card.description}
                      </Typography>
                      <Stack spacing={1} sx={{ mt: 'auto', pt: 1 }}>
                        {card.links.map((link) => (
                          <Button
                            key={link.label}
                            variant="contained"
                            endIcon={<LaunchIcon />}
                            onClick={() => openLink(link.url)}
                            sx={{
                              backgroundColor: BRAND,
                              color: 'white',
                              py: 1.25,
                              fontWeight: 'bold',
                              '&:hover': {
                                backgroundColor: BRAND_DARK,
                              },
                            }}
                            fullWidth
                          >
                            {link.label}
                          </Button>
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>

        <Box sx={{ mt: 8 }}>
          <Paper
            elevation={0}
            sx={{
              p: 5,
              background: `linear-gradient(135deg, #fff 0%, ${BRAND} 100%)`,
              color: 'white',
              borderRadius: 3,
            }}
          >
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <FavoriteIcon sx={{ fontSize: 40, opacity: 0.9 }} />
            </Box>
            <Typography
              variant="h4"
              component="h3"
              sx={{ mb: 3, textAlign: 'center', fontWeight: 600 }}
            >
              Grow With Your Sisters
            </Typography>
            <Typography
              variant="h6"
              sx={{
                textAlign: 'center',
                opacity: 0.9,
                maxWidth: 800,
                mx: 'auto',
              }}
            >
              Membership in P.E.O. is strongest when every sister feels
              welcomed, connected, and ready to invite the next generation into
              the Sisterhood.
            </Typography>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default MembershipResources;
